import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "./supabase";
import { lerTodas } from "./paginacao";

export interface Deposito {
  id: string;
  nome: string;
  tiny_deposito_id: number | null;
  ativo: boolean;
}

export function useDepositos() {
  return useQuery({
    queryKey: ["depositos"],
    queryFn: async () => {
      const { data, error } = await supabase.from("depositos").select("*").order("nome");
      if (error) throw error;
      return data as Deposito[];
    },
    staleTime: 5 * 60_000,
  });
}

/** Lê uma view/tabela inteira (em páginas, ver lerTodas) com filtro opcional por depósito. */
export async function lerTabela<T>(nome: string, deposito?: string | null, ordem?: { coluna: string; asc?: boolean }) {
  return lerTodas<T>((de, ate) => {
    let q = supabase.from(nome).select("*");
    if (deposito) q = q.eq("deposito_id", deposito);
    if (ordem) q = q.order(ordem.coluna, { ascending: ordem.asc ?? true });
    return q.range(de, ate);
  });
}

/** Depósito escolhido na tela: começa no depósito do usuário (ou Pinhais) e o líder pode trocar. */
export function useDepositoEscolhido(depositoPadrao: string | null | undefined) {
  const depositos = useDepositos();
  const [deposito, setDeposito] = useState<string | null>(null);
  useEffect(() => {
    if (!deposito && depositos.data?.length) {
      setDeposito(depositoPadrao ?? depositos.data.find((d) => d.nome === "Pinhais")?.id ?? depositos.data[0].id);
    }
  }, [deposito, depositos.data, depositoPadrao]);
  return { depositos: depositos.data ?? [], deposito, setDeposito };
}

/** Valor de uma configuração do admin (tabela configuracoes, legível por qualquer usuário ativo). */
export function useConfiguracao<T>(chave: string, padrao: T) {
  return useQuery({
    queryKey: ["config", chave],
    queryFn: async () => {
      const { data, error } = await supabase.from("configuracoes").select("valor").eq("chave", chave).maybeSingle();
      if (error) throw error;
      return (data?.valor ?? padrao) as T;
    },
    staleTime: 60_000,
  });
}

/** Texto de busca seguro para os filtros do PostgREST (or/ilike): tira vírgula, parênteses e curingas. */
export const limparBusca = (texto: string) => texto.replace(/[%_,()*\\:"']/g, " ").replace(/\s+/g, " ").trim();
